import React, { useState } from 'react';
import { api, resolveImg } from '../../api';
import ImageUploader from './ImageUploader';
import { Plus, Trash2, Save, X, Pencil, Shield, Feather } from 'lucide-react';

const empty = { img: '', nameFr: '', nameEn: '', category: 'acier', priceFrom: '', order: 0 };

const ProthesesAdmin = ({ content, refresh }) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState('all'); // 'all' | 'acier' | 'titane'

  const startNew = (cat = 'acier') => {
    setForm({ ...empty, category: cat, order: (content.protheses?.length || 0) });
    setEditing('new');
  };
  const startEdit = (p) => {
    setForm({
      img: p.img || '',
      nameFr: p.nameFr || '',
      nameEn: p.nameEn || '',
      category: p.category || 'acier',
      priceFrom: p.priceFrom || '',
      order: p.order || 0,
    });
    setEditing(p.id);
  };
  const cancel = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    if (!form.img) { alert('Veuillez téléverser une photo'); return; }
    if (editing === 'new') {
      await api.post('/admin/protheses', form);
    } else {
      await api.put(`/admin/protheses/${editing}`, form);
    }
    await refresh();
    cancel();
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer cette prothèse ?')) return;
    await api.delete(`/admin/protheses/${id}`);
    await refresh();
  };

  const all = content.protheses || [];
  const items = filter === 'all' ? all : all.filter((p) => p.category === filter);
  const acierCount = all.filter((p) => p.category === 'acier').length;
  const titaneCount = all.filter((p) => p.category === 'titane').length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="h-display text-[32px]">Prothèses médicales</h2>
          <p className="text-[13px] text-[#8A8275] mt-1">
            Acier 316L : {acierCount} · Titane G23 : {titaneCount}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => startNew('acier')} className="btn-ghost" style={{ padding: '10px 18px' }}>
            <Plus size={14} /> Acier
          </button>
          <button onClick={() => startNew('titane')} className="btn-primary">
            <Plus size={14} /> Titane
          </button>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 mb-8 border-b border-[#E8E2D5] pb-4">
        {[
          { v: 'all', l: `Toutes (${all.length})` },
          { v: 'acier', l: `Acier (${acierCount})` },
          { v: 'titane', l: `Titane (${titaneCount})` },
        ].map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            className={`px-4 py-2 text-[10px] tracking-[0.28em] uppercase border transition-colors ${
              filter === f.v
                ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
                : 'border-[#E8E2D5] text-[#1A1A1A] hover:border-[#1A1A1A]'
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {editing && (
        <div className="bg-[#FAF8F4] border border-[#957340] p-6 mb-8">
          <div className="flex items-center justify-between mb-5">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#957340]">
              {editing === 'new' ? 'Nouvelle prothèse' : 'Modifier'}
            </div>
            <button onClick={cancel}><X size={16} /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <ImageUploader value={form.img} onChange={(v) => setForm({ ...form, img: v })} label="Photo de la prothèse" />
            </div>

            <div className="md:col-span-2">
              <span className="text-[10px] tracking-[0.28em] uppercase text-[#8A8275]">Catégorie</span>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, category: 'acier' })}
                  className={`p-4 border text-left transition-colors ${
                    form.category === 'acier'
                      ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
                      : 'bg-white border-[#E8E2D5] hover:border-[#1A1A1A]'
                  }`}
                >
                  <Shield size={14} className="mb-2" />
                  <div className="serif text-[16px]">Acier 316L</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase opacity-70 mt-1">Chirurgical</div>
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, category: 'titane' })}
                  className={`p-4 border text-left transition-colors ${
                    form.category === 'titane'
                      ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
                      : 'bg-white border-[#E8E2D5] hover:border-[#1A1A1A]'
                  }`}
                >
                  <Feather size={14} className="mb-2" />
                  <div className="serif text-[16px]">Titane G23</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase opacity-70 mt-1">Médical</div>
                </button>
              </div>
            </div>

            <Inp label="Nom (FR)" value={form.nameFr} onChange={(v) => setForm({ ...form, nameFr: v })} />
            <Inp label="Nom (EN)" value={form.nameEn} onChange={(v) => setForm({ ...form, nameEn: v })} />
            <Inp label="Prix (ex: 25€) — optionnel" value={form.priceFrom} onChange={(v) => setForm({ ...form, priceFrom: v })} />
            <Inp label="Ordre d'affichage" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: parseInt(v) || 0 })} />
          </div>
          <div className="mt-6 flex gap-3">
            <button onClick={save} className="btn-primary"><Save size={14} /> Enregistrer</button>
            <button onClick={cancel} className="btn-ghost">Annuler</button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#E8E2D5] bg-[#FAF8F4]">
          <p className="text-[14px] text-[#8A8275]">
            Aucune prothèse pour l'instant. Cliquez sur « Acier » ou « Titane » en haut pour ajouter votre premier modèle.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
          {items.map((p) => (
            <div key={p.id} className="bg-white border border-[#E8E2D5] overflow-hidden">
              <div className="aspect-square bg-[#FAF8F4]">
                {p.img && <img src={resolveImg(p.img)} alt={p.nameFr} className="w-full h-full object-cover" />}
              </div>
              <div className="p-4">
                <div className="flex items-baseline justify-between">
                  <div className="serif text-[16px] text-[#1A1A1A]">{p.nameFr || '—'}</div>
                  {p.priceFrom && <div className="display text-[16px] text-[#957340]">{p.priceFrom}</div>}
                </div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#957340] mt-1">
                  {p.category === 'titane' ? 'Titane G23' : 'Acier 316L'}
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => startEdit(p)} className="flex-1 border border-[#1A1A1A] py-2 text-[10px] tracking-[0.28em] uppercase hover:bg-[#1A1A1A] hover:text-white transition-colors flex items-center justify-center gap-2">
                    <Pencil size={12} /> Modifier
                  </button>
                  <button onClick={() => del(p.id)} className="px-3 py-2 border border-[#E8E2D5] text-[#8A8275] hover:text-red-600 hover:border-red-600 transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Inp = ({ label, value, onChange, type = 'text' }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.28em] uppercase text-[#8A8275]">{label}</span>
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full bg-white border border-[#E8E2D5] focus:border-[#957340] outline-none p-3 text-[14px] transition-colors"
    />
  </label>
);

export default ProthesesAdmin;
